import { DashboardItemService, VisConfig } from '../dashboard_item.service';
import { Operations } from '../DashboardItemPostAggregationOptions';
import { isNil } from 'lodash';
import { getAggregationSuffix } from 'app/overview/dashboard/dashboard_data_transformers';
import Immutable from 'immutable';

interface FilterKeyOption {
    label: string;
    value: string;
}

export class DashboardItemPostAggHelpers {
    private _dashboardItemService: DashboardItemService;
    constructor(dashboardItemService: DashboardItemService) {
        this._dashboardItemService = dashboardItemService;
    }

    public generateFilterKeyOptions(visConfig: VisConfig): Array<{ label: string; value: string }> {
        const options = this._dashboardItemService.getFieldsForDataSource(visConfig.dataSource);
        return options.map((option) => {
            if (option === visConfig.dataField) {
                return _defaultOption(option, visConfig);
            } else {
                return _nonDefaultOption(option, visConfig);
            }
        });
    }

    public getValuesForPostAggregateFilter(visConfig: VisConfig): {
        value: number;
        comparisonOperator: keyof Operations;
        filterKey: string;
        aggregationType: string;
    } {
        if (
            _postAggregateFilterHasRequiredKeys(visConfig) &&
            this.filterKeyInValidOptions(visConfig)
        ) {
            const value = visConfig.postAggregationFilters.filterValue as number;
            const comparisonOperator = visConfig.postAggregationFilters.comparisonOperator;
            const filterKey = visConfig.postAggregationFilters.filterKey;
            const aggregationType = visConfig.aggregationType;
            return { value, comparisonOperator, filterKey, aggregationType };
        } else {
            return _setPostAggregateDefaults(visConfig);
        }
    }

    private filterKeyInValidOptions(visConfig: VisConfig): boolean {
        const allValidFilterKeyOptions = this._dashboardItemService.getFieldsForDataSource(
            visConfig.dataSource,
        );
        return allValidFilterKeyOptions.includes(visConfig.postAggregationFilters.filterKey);
    }
}

function _defaultOption(option: string, visConfig: VisConfig): FilterKeyOption {
    return {
        label:
            getAggregationSuffix(visConfig.aggregationType).substr(1) +
            ' ' +
            DashboardItemService.fieldDetails.getIn([option, 'name']) +
            ' (default)',
        value: option,
    };
}

function _nonDefaultOption(option: string, visConfig: VisConfig): FilterKeyOption {
    return {
        label:
            getAggregationSuffix(visConfig.aggregationType).substr(1) +
            ' ' +
            DashboardItemService.fieldDetails.getIn([option, 'name']),
        value: option,
    };
}

export function shouldUseAPostAggregateFilter(visConfig: VisConfig): boolean {
    return (
        _postAggregateFilterHasRequiredKeys(visConfig) &&
        !isNil(visConfig.postAggregationFilters.comparisonOperator) &&
        !isNil(visConfig.postAggregationFilters.filterKey)
    );
}

function _postAggregateFilterHasRequiredKeys(visConfig: VisConfig): boolean {
    return (
        visConfig &&
        !isNil(visConfig.postAggregationFilters) &&
        !isNil(visConfig.postAggregationFilters.filterKey)
    );
}

export function findDataSourceUnit(field: string, aggregation: string): string {
    if (!aggregation) {
        console.warn(`aggregation for ${field} was nil!`);
        return '';
    }
    if (aggregation === 'count') {
        // HACK
        // Never return any units for count aggregation type
        return '';
    }
    const fieldDetails: Immutable.Map<string, any> = DashboardItemService.fieldDetails.get(field);
    const aggregationTypes: Array<string> =
        (fieldDetails && fieldDetails.get('aggregationTypes').toJS()) || [];
    let aggregationIdx = aggregationTypes.indexOf(aggregation);
    if (aggregationIdx === -1) {
        // HACK
        // Default to the first thing in the aggregation type list
        aggregationIdx = 0;
    }
    const aggregationTypesSuffix: Array<string> =
        (fieldDetails && fieldDetails.get('aggregationTypesSuffix').toJS()) || [];
    return aggregationTypesSuffix.length > 0 ? aggregationTypesSuffix[aggregationIdx] : '';
}

function _setPostAggregateDefaults(visConfig: VisConfig): {
    value: number;
    comparisonOperator: keyof Operations;
    filterKey: string;
    aggregationType;
} {
    const value = 0;
    const comparisonOperator = null;
    const filterKey = visConfig.dataField;
    const aggregationType = visConfig.aggregationType;
    return { value, comparisonOperator, filterKey, aggregationType };
}
